import { BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/dist/query/react';
import { API, Auth } from 'aws-amplify';

import * as AllTypes from 'clipsal-cortex-types/src/api';
import { getCurrentAuthSession } from 'clipsal-cortex-utils/src/auth/cached-session';

import { ENV_TYPE_SHORTHAND } from '../../env-type';
import { REGION_SHORTHAND } from '../../region-type';
import {
  trackDynatraceAuthorizationRejected,
  trackDynatraceEimTokenRefreshResult,
  trackDynatraceHeliosAuthorizationResult,
} from '../../utils/native/dynatrace-events';

const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;

/**
 * Performs a GET request to a provided name/resource path on the API.
 *
 * @param path The path from the end point resource
 */
export async function get<T>(path: string): Promise<T> {
  return await withAuthorizationTelemetry('GET', path, async () => {
    const { token } = await getCurrentSession();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    return await API.get('site', path, { headers });
  });
}

/**
 * Performs a POST request to a provided name/resource path on the API.
 *
 * @param path The path of the resource.
 * @param body The request body.
 */
export async function post<T>(path: string, body: object): Promise<T> {
  return await withAuthorizationTelemetry('POST', path, async () => {
    const { token } = await getCurrentSession();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-api-key': viteEnv?.[`VITE_${REGION_SHORTHAND}_${ENV_TYPE_SHORTHAND}_APIGW_API_KEY`] as string,
    };

    return await API.post('site', path, { headers, body });
  });
}

/**
 * Performs a POST request to a provided public name/resource path on the API that doesn't require auth.
 *
 * @param path The path of the resource.
 * @param body The request body.
 */
export async function postPublic<T>(path: string, body: object): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-api-key': viteEnv?.[`VITE_${REGION_SHORTHAND}_${ENV_TYPE_SHORTHAND}_APIGW_API_KEY`] as string,
  };
  return await API.post('site', path, {
    headers,
    body,
  });
}

/**
 * Performs a PATCH request to a provided name/resource path on the API.
 *
 * @param path The path of the resource.
 * @param body The request body.
 */
export async function patch<T>(path: string, body: object): Promise<T> {
  const { token } = await getCurrentSession();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-api-key': viteEnv?.[`VITE_${REGION_SHORTHAND}_${ENV_TYPE_SHORTHAND}_APIGW_API_KEY`] as string,
  };

  return await withAuthorizationTelemetry('PATCH', path, () => API.patch('site', path, { headers, body }));
}

/**
 * Performs a DELETE request to a provided name/resource path on the API.
 *
 * @param path The path of the resource.
 * @param body The request body.
 */
export async function del<T>(path: string, body: object): Promise<T> {
  const { token } = await getCurrentSession();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-api-key': viteEnv?.[`VITE_${REGION_SHORTHAND}_${ENV_TYPE_SHORTHAND}_APIGW_API_KEY`] as string,
  };

  return await withAuthorizationTelemetry('DELETE', path, () => API.del('site', path, { headers, body }));
}

/**
 * Performs a PUT request to a provided name/resource path on the API.
 *
 * @param path The path of the resource.
 * @param body The request body.
 */
export async function put<T>(path: string, body: object): Promise<T> {
  const { token } = await getCurrentSession();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-api-key': viteEnv?.[`VITE_${REGION_SHORTHAND}_${ENV_TYPE_SHORTHAND}_APIGW_API_KEY`] as string,
  };

  return await withAuthorizationTelemetry('PUT', path, () => API.put('site', path, { headers, body }));
}

export type ApiFunction<T> = ((path: string, body: object) => Promise<T>) | ((path: string) => Promise<T>);
export type APIMethods = 'GET' | 'POST' | 'POST_PUBLIC' | 'DELETE' | 'PATCH' | 'PUT';
export type QueryArgs = string | (FetchArgs & { method: APIMethods });
type AllApiTypes = typeof AllTypes;

const mapApiMethodToApiFunction: Record<APIMethods, ApiFunction<AllApiTypes>> = {
  GET: get,
  POST: post,
  POST_PUBLIC: postPublic,
  PUT: put,
  DELETE: del,
  PATCH: patch,
};

export type ErrorResponse = {
  detail: string;
  code: string;
  status_code: number;
  errors?: {
    error: { code: string; message: string }[];
  };
  message?: string;
};

export type NetworkError = {
  status: number;
  response?: {
    status: number;
    data: ErrorResponse;
  };
  message: string;
};

type AuthorizationApiOperation = 'user_profile' | 'eim_token_refresh' | 'other_protected_api';

function getAuthorizationApiOperation(path: string): AuthorizationApiOperation {
  if (path === '/v1/user') return 'user_profile';
  if (path === '/v1/eim_refresh_token') return 'eim_token_refresh';
  return 'other_protected_api';
}

async function withAuthorizationTelemetry<T>(method: string, path: string, request: () => Promise<T>): Promise<T> {
  const apiOperation = getAuthorizationApiOperation(path);
  try {
    const data = await request();
    if (apiOperation === 'eim_token_refresh') trackDynatraceEimTokenRefreshResult('success');
    return data;
  } catch (error: unknown) {
    const networkError = error as NetworkError;
    const status = networkError?.status ?? networkError?.response?.status;
    if (apiOperation === 'eim_token_refresh') {
      trackDynatraceEimTokenRefreshResult('failure', status);
    } else if (apiOperation === 'user_profile') {
      trackDynatraceHeliosAuthorizationResult('failure', {
        requestMethod: method,
        apiOperation,
        failureReason: 'request_failed',
        ...(status !== undefined ? { httpStatus: status } : {}),
      });
    } else if (status === 401 || status === 403) {
      trackDynatraceAuthorizationRejected(method, status, apiOperation);
    }
    throw error;
  }
}

export type RTKQError = {
  status: number;
  message: string;
  originalError: NetworkError;
};

export const customBaseQuery = (): BaseQueryFn<QueryArgs, unknown, RTKQError> => async (args) => {
  try {
    const url = typeof args === 'string' ? args : args.url;
    const method = typeof args === 'string' ? 'GET' : args.method;
    const body = typeof args === 'string' ? undefined : args.body;
    const apiFunction = mapApiMethodToApiFunction[method || 'GET'];
    const data = await apiFunction(url, body);
    return { data };
  } catch (e: unknown) {
    const error = e as NetworkError;

    return {
      error: {
        status: error?.status ?? error?.response?.status ?? 500,
        message: error?.message ?? error?.response?.data?.detail ?? '',
        originalError: error,
      },
    };
  }
};

/**
 * In a monorepo setup, Amplify does not work properly when placed in a shared folder.
 * To avoid issues, Amplify Auth must be passed explicitly as a parameter.
 */
export const getCurrentSession = async () => {
  return await getCurrentAuthSession(Auth);
};

/**
 * Opens an authenticated SSE-style fetch stream to the given fully-qualified URL.
 * Calls `onLine` for each newline-delimited, non-empty data line received.
 * Handles both raw JSON lines and SSE `data:` prefixed lines.
 * Honours the provided AbortSignal for cleanup.
 *
 * @param url - The fully-qualified stream endpoint URL.
 * @param onLine - Callback invoked with each non-empty trimmed line.
 * @param signal - AbortSignal used to cancel the stream on unmount/cleanup.
 */
export async function streamEvents(
  url: string,
  onLine: (line: string) => void,
  signal: AbortSignal,
  onConnected?: () => void
): Promise<void> {
  const { token } = await getCurrentSession();

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });

  if (!response.ok || !response.body) throw new Error('SSE connection failed');
  onConnected?.();

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const trimmed = line.startsWith('data:') ? line.slice(5).trim() : line.trim();
      if (trimmed) onLine(trimmed);
    }
  }
  // Flush any remaining content that didn't end with a newline (e.g. last line of a completed stream).
  const remaining = buffer.startsWith('data:') ? buffer.slice(5).trim() : buffer.trim();
  if (remaining) onLine(remaining);
}
